import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Avatar,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonButtons from "./PatientButtons";
import { toast } from "react-toastify";
import { TrackerRecordData, TrackerData } from "../../types/Patient";
import AddressList from "../addresses/AddressList";
import { imageBaseUrl } from "../../api/Index";
import EmergencyContactList from "../emergencycontacts/EmergencyContactList";
import { transactionEnum } from "../../utils/AppEnum";
import {
  getPendingTaskIds,
  getWorkflowData,
  registerPatient,
} from "../../api/WorkflowApi";
import RegistrationForm from "../registration/RegistrationForm";
import { ListData, MasterListData } from "../../types/List";
import { UserData } from "../../types/User";

type ComponentProps = {
  handleBack: () => void;
  detailData: UserData;
  registeredIds: TrackerRecordData[];
  masterList: MasterListData[];
};

const PatientDetail: React.FC<ComponentProps> = ({
  handleBack,
  detailData,
  registeredIds,
  masterList,
}) => {
  const [pendingTaskIds, setPendingTaskIds] = useState<Number[]>([]);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [workflow, setWorkflow] = useState<ListData[]>([]);
  const [expanded, setExpanded] = useState<string | false>("detail");
  const [register, setRegister] = useState(false);
  const [allExpanded, setAllExpanded] = useState(false);

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
      setAllExpanded(false);
    };

  const handleExpandAll = () => {
    setExpanded(false);
    setAllExpanded(true);
  };

  const handleCollapseAll = () => {
    setExpanded(false);
    setAllExpanded(false);
  };

  const handleRegister = async () => {
    setRegister(true);
  };

  const handleClose = async () => {
    setRegister(false);
  };

  const loadData = async () => {
    const resp = await getWorkflowData(transactionEnum.OPD);
    setWorkflow(resp);

    const taskIds = await getPendingTaskIds(transactionEnum.OPD);
    setPendingTaskIds(taskIds);
  };
  const handleSubmit = async (data: Partial<TrackerData>) => {
    data.recordId = detailData.id;
    const resp = await registerPatient(data);
    if (resp == null) {
      toast.error("Failed to  register patient.");
      return;
    }
    toast.success("Registered successfully.");
    handleBack();
  };

  useEffect(() => {
    if (detailData.profileImagePath) {
      setImageUrl(`${imageBaseUrl}${detailData.profileImagePath}`);
    }
  }, [detailData.profileImagePath]);

  useEffect(() => {
    loadData();
  }, []);
  return (
    <>
      {register && (
        <RegistrationForm
          onSubmit={handleSubmit}
          onClose={handleClose}
          workflowData={workflow}
        />
      )}
      {!register && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            p: 3,
            minHeight: "100vh",
          }}
        >
          {/* Left Column - 80% */}
          <Box sx={{ flex: { xs: "1 1 100%", md: "0 0 80%" } }}>
            <Paper
              elevation={2}
              sx={{ p: 3, borderRadius: 2, backgroundColor: "#fff" }}
            >
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                Details of {detailData.firstName}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Expand/Collapse All Buttons */}
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleExpandAll}
                >
                  Expand All
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCollapseAll}
                >
                  Collapse All
                </Button>
              </Box>

              {/* Accordion Panels */}
              <Accordion
                expanded={allExpanded || expanded === "detail"}
                onChange={handleChange("detail")}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ backgroundColor: "#d0e4f5" }}
                >
                  <Typography variant="subtitle1">Detail</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      alignItems: { xs: "center", md: "flex-start" },
                      justifyContent: "center",
                      gap: 3,
                      p: 2,
                    }}
                  >
                    {/* Left Side - Avatar & Name */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: { xs: "100%", md: "30%" },
                        textAlign: "center",
                      }}
                    >
                      <Avatar
                        alt={detailData.firstName}
                        src={imageUrl ?? "/images/default.jpg"}
                        sx={{
                          width: 200,
                          height: 200,
                          mb: 1,
                          boxShadow: 3,
                        }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {detailData.firstName} {detailData.middleName}{" "}
                        {detailData.lastName}
                      </Typography>
                      {registeredIds.some(
                        (r) => r.recordId === detailData.id
                      ) && (
                        <Typography
                          variant="h6"
                          sx={{
                            // fontWeight: "bold",
                            fontStyle: "italic",
                            color: "#d7670cff",
                          }}
                        >
                          {`Reg. No. ${
                            registeredIds.find(
                              (r) => r.recordId === detailData.id
                            )?.trackerNo ?? ""
                          }`}
                        </Typography>
                      )}
                    </Box>

                    {/* Right Side - Info Fields */}
                    <Box
                      sx={{
                        width: { xs: "100%", md: "60%" },
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 3,
                      }}
                    >
                      {[
                        { label: "Email", value: detailData.email },
                        { label: "Role", value: detailData.roles.name },
                        {
                          label: "Gender",
                          value: masterList.find(
                            (x) => x.id == detailData.genderId
                          )?.name,
                        },
                        {
                          label: "Date of Birth",
                          value: detailData.dateOfBirth
                            ? new Date(
                                detailData.dateOfBirth
                              ).toLocaleDateString()
                            : "N/A",
                        },
                        {
                          label: "Primary Phone",
                          value: detailData.primaryPhone,
                        },
                        {
                          label: "Secondary Phone",
                          value: detailData.secondaryPhone,
                        },
                        {
                          label: "National ID",
                          value: detailData.nationalIdNumber,
                        },
                        {
                          label: "Status",
                          value: detailData.isActive ? "Active" : "Inactive",
                        },
                      ].map((item, index) => (
                        <Box
                          key={index}
                          sx={{ display: "flex", flexDirection: "column" }}
                        >
                          <Box
                            sx={{
                              borderBottom: "2px solid #ccc",
                              paddingBottom: "6px",
                              fontSize: "1rem",
                              color: "text.primary",
                              minHeight: "32px",
                            }}
                          >
                            {item.value || "—"}
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontWeight: 500,
                              marginTop: "4px",
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            {item.label}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={allExpanded || expanded === "address"}
                onChange={handleChange("address")}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ backgroundColor: "#d0e4f5" }}
                >
                  <Typography variant="subtitle1">Address</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <AddressList detailData={detailData} />
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={allExpanded || expanded === "emergency"}
                onChange={handleChange("emergency")}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ backgroundColor: "#d0e4f5" }}
                >
                  <Typography variant="subtitle1">Emergency Contact</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <EmergencyContactList detailData={detailData} />
                </AccordionDetails>
              </Accordion>
            </Paper>
          </Box>

          {/* Right Column - 20% */}
          <Box sx={{ flex: { xs: "1 1 100%", md: "0 0 20%" } }}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                backgroundColor: "#fff",
              }}
            >
              <PersonButtons
                handleBack={handleBack}
                handleRegister={handleRegister}
                detailData={detailData}
                registeredIds={registeredIds}
              />
            </Paper>
          </Box>
        </Box>
      )}
    </>
  );
};

export default PatientDetail;
